import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getPieceStats } from '@/services/revenue.service';
import { columnSorter, formatDate, formatNumber } from '@/utils/formatters';
import { Space } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';

const columns = [
  {
    title: 'No.',
    dataIndex: 'no',
    key: 'no',
    sorter: columnSorter('no'),
    render: (id) => (id ? id : '-'),
  },
  {
    title: 'ISDN',
    dataIndex: 'isdn',
    key: 'isdn',
    sorter: columnSorter('isdn'),
    render: (isdn) => (isdn ? isdn : '-'),
  },
  {
    title: 'Total Pieces',
    dataIndex: 'total_pieces',
    key: 'total_pieces',
    sorter: columnSorter('total_pieces'),
    render: (total_pieces) => (total_pieces ? formatNumber(total_pieces) : '0'),
  },
  {
    title: 'Report Month',
    dataIndex: 'report_month',
    key: 'report_month',
    sorter: columnSorter('report_month'),
    render: (report_month) => (report_month ? formatDate(report_month, 'YYYY/MM') : '-'),
  },
  {
    title: 'Last Updated',
    dataIndex: 'latest_updated_at',
    key: 'latest_updated_at',
    sorter: columnSorter('latest_updated_at'),
    render: (latest_updated_at) =>
      latest_updated_at ? formatDate(latest_updated_at, 'DD/MM/YYYY HH:mm:ss') : '-',
  },
];

const CURRENT_MONTH = dayjs().format('YYYY-MM');

function PieceStats() {
  const [searchValue, setSearchValue] = useState(null);
  const [reportMonth, setReportMonth] = useState(CURRENT_MONTH);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const offset = (pageIndex - 1) * pageSize + 1;
  const [query, setQuery] = useState({
    report_month: reportMonth,
    isdn: null,
  });

  const {
    data: pieceStatsData,
    isLoading,
    refetch,
    isRefetching,
  } = usePaginatedQuery(
    () =>
      getPieceStats({
        ...query,
        limit: pageSize,
        offset: offset,
      }),
    ['piece-stats', query, pageSize, offset]
  );

  const handleQuery = () => {
    setQuery({
      isdn: searchValue,
      report_month: reportMonth,
    });
    setPageIndex(1);
  };

  const handleReset = () => {
    setSearchValue(null);
    setReportMonth(CURRENT_MONTH);
    setQuery({
      isdn: null,
      report_month: CURRENT_MONTH,
    });
    setPageIndex(1);
  };

  const dataTable = useMemo(() => {
    if (!pieceStatsData?.data?.length || !Array.isArray(pieceStatsData.data)) return [];
    return pieceStatsData.data.map((item, index) => ({
      ...item,
      no: index + 1 + (pageIndex - 1) * pageSize,
    }));
  }, [pieceStatsData, pageIndex, pageSize]);

  return (
    <Space className="w-full" direction="vertical">
      <AppPageTitle title="Piece Statistics" />

      <AppTableQuery
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        time={reportMonth}
        setTime={setReportMonth}
        timePlaceholder="Report month"
        timeLabel="Report Month"
        hasDatePicker
        picker="month"
        hasFilter={false}
        handleQuery={handleQuery}
        handleReset={handleReset}
        searchPlaceholder="Search by ISDN"
      />

      <AppTable
        columns={columns}
        dataSource={dataTable || []}
        totalItems={pieceStatsData?.total || 0}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setPageIndex(page);
        }}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
        }}
        loading={isLoading || isRefetching}
        onRefresh={refetch}
      />
    </Space>
  );
}

export default PieceStats;
